/**
 * Chart Data Sampling Utilities
 * Handles smart downsampling and X-axis label display for charts
 */

export interface DataPoint {
  date: string;
  value: number;
}

export interface SamplingConfig {
  maxDataPoints: number;
  maxLabels: number;
}

/**
 * Get optimal sampling configuration based on timeframe
 */
export const getSamplingConfig = (
  timeframe: "today" | "7-days" | "14-days" | "30-days" | "all-time" | string
): SamplingConfig => {
  switch (timeframe) {
    case "today":
      return { maxDataPoints: 24, maxLabels: 12 }; // Show hourly data (24 hours, 12 labels for readability)
    case "7-days":
      return { maxDataPoints: 7, maxLabels: 7 }; // Show all days
    case "14-days":
      return { maxDataPoints: 14, maxLabels: 7 }; // Show every other day
    case "30-days":
      return { maxDataPoints: 30, maxLabels: 10 }; // Show ~3 days per label
    case "all-time":
      return { maxDataPoints: 50, maxLabels: 30 }; // Show maximum 30 labels when fully zoomed out
    default:
      return { maxDataPoints: 30, maxLabels: 10 };
  }
};

/**
 * Downsample data points to optimal number for display
 * Uses simple decimation (every Nth point) to maintain trend
 */
export const downsampleDataPoints = <T extends DataPoint>(
  dataPoints: T[],
  maxDataPoints: number
): T[] => {
  if (dataPoints.length <= maxDataPoints) {
    return dataPoints;
  }

  // Always include first and last points
  const result: T[] = [dataPoints[0]];
  
  if (dataPoints.length === 2) {
    return dataPoints;
  }

  // Calculate step size
  const step = (dataPoints.length - 1) / (maxDataPoints - 1);
  
  // Sample points evenly across the dataset
  for (let i = 1; i < maxDataPoints - 1; i++) {
    const index = Math.round(i * step);
    if (index > 0 && index < dataPoints.length - 1) {
      result.push(dataPoints[index]);
    }
  }
  
  // Always include last point
  result.push(dataPoints[dataPoints.length - 1]);
  
  return result;
};

/**
 * Get indices of labels to show on X-axis
 * Returns array of indices that should have visible labels
 */
export const getLabelIndices = (
  totalDataPoints: number,
  maxLabels: number
): number[] => {
  if (totalDataPoints <= maxLabels) {
    // Show all labels
    return Array.from({ length: totalDataPoints }, (_, i) => i);
  }

  const result: number[] = [0]; // Always show first
  const labelInterval = Math.ceil(totalDataPoints / maxLabels);
  
  // Add intermediate labels
  for (let i = labelInterval; i < totalDataPoints - 1; i += labelInterval) {
    result.push(i);
  }
  
  // Always show last
  if (totalDataPoints > 1) {
    result.push(totalDataPoints - 1);
  }
  
  return result;
};

/**
 * Get X-axis label formatter that shows labels at optimal intervals
 * For category-based charts, ApexCharts calls: formatter(value, opts) where opts.dataPointIndex is the index
 */
export const getXAxisLabelFormatter = (
  totalDataPoints: number,
  maxLabels: number,
  isDailyFormat: boolean
) => {
  if (totalDataPoints <= maxLabels) {
    // Show all labels
    return function(value: string, opts?: any): string {
      return value;
    };
  }

  // Get which indices should show labels
  const labelIndices = getLabelIndices(totalDataPoints, maxLabels);
  const labelSet = new Set(labelIndices);
  
  return function(value: string, opts?: any): string {
    // For category-based charts, ApexCharts passes opts with dataPointIndex
    let index: number | undefined;
    
    // Try to get index from opts.dataPointIndex
    if (opts && typeof opts === 'object') {
      if ('dataPointIndex' in opts && typeof opts.dataPointIndex === 'number') {
        index = opts.dataPointIndex;
      }
      // Sometimes ApexCharts passes the index directly as the second parameter
      else if ('seriesIndex' in opts) {
        // Not the right property, but let's check other possibilities
      }
    }
    
    // If we still don't have an index, try to find it by matching the value
    // This is a fallback for when dataPointIndex isn't available
    if (index === undefined) {
      // Try to find the index by matching the value in the categories array
      // We'll need to access the categories, but we don't have that here
      // So we'll use a different approach - hide all labels we can't identify
      return "";
    }
    
    // Only show label if index is in our label set
    return labelSet.has(index) ? value : "";
  };
};

/**
 * Get X-axis label configuration for ApexCharts
 * Returns both formatter and other label options
 * @param categories - Optional array of category values for fallback index matching
 */
export const getXAxisLabelConfig = (
  totalDataPoints: number,
  maxLabels: number,
  isDailyFormat: boolean,
  categories?: string[]
) => {
  // Cap maxLabels at 30 to prevent overcrowding on x-axis
  const cappedMaxLabels = Math.min(maxLabels, 30);
  
  // Get which indices should show labels
  const labelIndices = totalDataPoints <= cappedMaxLabels
    ? Array.from({ length: totalDataPoints }, (_, i) => i)
    : getLabelIndices(totalDataPoints, cappedMaxLabels);
  const labelSet = new Set(labelIndices);
  
  // Create a more reliable formatter that can match by value if index isn't available
  const formatter = function(value: string, opts?: any): string {
    let index: number | undefined;
    
    // Try to get index from opts.dataPointIndex (most reliable)
    if (opts && typeof opts === 'object' && 'dataPointIndex' in opts) {
      index = opts.dataPointIndex as number;
    }
    // Fallback: try to find index by matching value in categories
    else if (categories && Array.isArray(categories)) {
      index = categories.indexOf(value);
    }
    
    // If we have an index, check if it should be shown
    if (index !== undefined && index >= 0) {
      return labelSet.has(index) ? value : "";
    }
    
    // If we can't determine index, hide the label to be safe
    return "";
  };
  
  return {
    formatter,
    // Use hideOverlappingLabels to help reduce clutter
    hideOverlappingLabels: true,
    // Rotate labels to prevent overlap
    rotate: -45,
    rotateAlways: false,
    maxHeight: 60,
    showDuplicates: false,
    // Style for labels
    style: {
      colors: "#c8cfca",
      fontSize: "12px",
    },
  };
};

/**
 * Determine if date format is daily (YYYY-MM-DD) vs monthly (YYYY-MM).
 * Hourly buckets (`YYYY-MM-DD HH:00`) are not daily.
 */
export const isDailyDateFormat = (dateString: string): boolean => {
  const s = String(dateString ?? "").trim();
  if (!s) return false;
  if (isHourlyChartBucketDate(s)) return false;
  // After chartBucketKeyFromPointDate, hourly becomes YYYY-MM-DD — still "daily" grain for range filters.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return true;
  // Legacy: prefix date with junk — treat as daily if it starts with a calendar day and isn't month-only.
  if (/^\d{4}-\d{2}-\d{2}/.test(s) && s.split("-").length >= 3) return true;
  return false;
};

/** Backend hourly chart buckets: `YYYY-MM-DD HH:00` or ISO datetime. */
export function isHourlyChartBucketDate(dateString: string): boolean {
  const s = String(dateString ?? "").trim();
  return /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(s);
}

export type ChartTimeGrain = "hourly" | "daily" | "weekly" | "monthly";

function normalizeChartTimeGrain(raw?: string | null): ChartTimeGrain | null {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (!s) return null;
  if (s === "hourly" || s === "hour" || s === "hours") return "hourly";
  if (s === "daily" || s === "day" || s === "days") return "daily";
  if (s === "weekly" || s === "week" || s === "weeks") return "weekly";
  if (s === "monthly" || s === "month" || s === "months") return "monthly";
  return null;
}

/** Infer grain from point date strings (hourly upgrade leaves `YYYY-MM-DD HH:00`). */
export function inferChartTimeGrainFromDates(dates: Array<string | null | undefined>): ChartTimeGrain | null {
  for (const raw of dates) {
    const s = String(raw ?? "").trim();
    if (!s) continue;
    if (isHourlyChartBucketDate(s)) return "hourly";
    if (/^\d{4}-\d{2}$/.test(s)) return "monthly";
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return "daily";
  }
  return null;
}

/**
 * Prefer response sampling/period/breakdown, then date-shape (BE auto-upgrade), then request grain.
 */
export function resolveChartTimeGrain(params: {
  responseGrain?: string | null;
  requestGrain?: string | null;
  dates?: Array<string | null | undefined>;
}): ChartTimeGrain {
  const fromResponse = normalizeChartTimeGrain(params.responseGrain);
  if (fromResponse) return fromResponse;

  const inferred = params.dates?.length
    ? inferChartTimeGrainFromDates(params.dates)
    : null;
  // Date shape catches FE still requesting daily while BE returns hourly buckets.
  if (inferred === "hourly") return "hourly";

  const fromRequest = normalizeChartTimeGrain(params.requestGrain);
  if (fromRequest) return fromRequest;

  if (inferred) return inferred;
  return "daily";
}

/** True when hourly points span more than one calendar day (need day+hour axis labels). */
export function hourlyChartSpansMultipleDays(dates: Array<string | null | undefined>): boolean {
  const days = new Set<string>();
  for (const raw of dates) {
    const s = String(raw ?? "").trim();
    if (!s) continue;
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      days.add(s.slice(0, 10));
      if (days.size > 1) return true;
    }
  }
  return false;
}

